import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Coins, IdCard, LayoutDashboard, Wallet } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchTransactions, type AdminTransaction } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A10.01 — Barcha tranzaksiyalar. GET /admin/transactions (backend `78bf6c0`).
 * delta = Take token (ichki, +/-). Provayder/valyuta/status = follow-up (endpoint).
 */
export function TransactionsScreen() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminTransaction>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/finance/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminTransaction>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'user',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'delta',
        header: t.finance.amount,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => (
          <span
            className={cn('block text-right font-mono tabular', r.delta >= 0 ? 'text-success' : 'text-danger')}
          >
            {r.delta >= 0 ? '+' : ''}
            {r.delta.toLocaleString()}
          </span>
        ),
      },
      { key: 'kind', header: t.col.type, type: 'text', width: 160 },
      { key: 'note', header: t.finance.note, type: 'longtext', minWidth: 180 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchTransactions, [])

  return (
    <>
      <PageHeader
        title={t.nav.finance}
        screenId="A10.01"
        verified
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/finance/dashboard"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <LayoutDashboard size={13} />
              {t.finance.dashboard}
            </Link>
            <Link
              to="/finance/wallets"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <Wallet size={13} />
              {t.finance.wallets}
            </Link>
          </div>
        }
      />
      <DataTable<AdminTransaction>
        screenId="A10.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Coins size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
